import React, { Suspense, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { LogOut } from 'lucide-react';
import { Images } from '@assets';
import { useAuth } from '@context/AuthContext';
import { useStudentTheme } from '@context/StudentThemeContext';
import type { StudentDashboardData } from '@services/student/studentQueries';
import { cx } from '../cx';
import { SPThemeProvider } from '../theme';
import { fade } from '../motion';
import { buildJourneyNav, legacyRedirect } from './navConfig';
import { JourneyNav, JourneyBottomNav } from './JourneyNav';
import { JourneyTopBar } from './JourneyTopBar';
import JourneyProfile from '@pages/StudentPortal/Journey/JourneyProfile';
import JourneySkillProfile from '@pages/StudentPortal/Journey/JourneySkillProfile';

interface JourneyShellProps {
  dashboard: StudentDashboardData | undefined;
}

/**
 * The Cohort-2 Student Portal shell. Sibling to the CLASSIC `StudentLayoutInner`
 * — selected by `StudentLayout` via `resolvePortalVersion`. Renders inside the
 * existing `StudentThemeProvider`, so the theme toggle + persistence are shared.
 */
export const JourneyShell: React.FC<JourneyShellProps> = ({ dashboard }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme } = useStudentTheme();
  const { t } = useTranslation('journey');
  const dark = theme === 'dark';

  const [navOpen, setNavOpen] = useState(false);
  // Desktop-only: collapse the fixed rail off-canvas to give the content its
  // width back (the Python Lab notebook especially). Persisted so it sticks.
  const [railCollapsed, setRailCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('spRailCollapsed') === '1'; } catch { return false; }
  });

  // Close the drawer on route change.
  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  useEffect(() => {
    try { localStorage.setItem('spRailCollapsed', railCollapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [railCollapsed]);

  // The top-bar hamburger: collapses the rail on desktop, opens the drawer below it.
  const handleToggleNav = () => {
    if (typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches) {
      setRailCollapsed((v) => !v);
    } else {
      setNavOpen((v) => !v);
    }
  };

  const [liveDashboard, setLiveDashboard] = useState<StudentDashboardData | undefined>(dashboard);

  useEffect(() => {
    setLiveDashboard(dashboard);
  }, [dashboard]);

  useEffect(() => {
    const handleProfileUpdate = (event: any) => {
      if (event?.detail?.profile_completion) {
        setLiveDashboard((prev) => prev ? {
          ...prev,
          profile_completion: event.detail.profile_completion,
        } : prev);
      }
    };
    window.addEventListener('student-profile-completion-updated', handleProfileUpdate);
    return () => window.removeEventListener('student-profile-completion-updated', handleProfileUpdate);
  }, []);

  const items = buildJourneyNav(liveDashboard);
  const isSkillProfileLocked = liveDashboard?.profile_completion ? !liveDashboard.profile_completion.is_complete : false;

  // A JOURNEY student who hits a CLASSIC-only URL (old bookmark, redirect target) → Journey equivalent.
  const redirect = legacyRedirect(location.pathname);
  if (redirect) return <Navigate to={redirect} replace />;

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  return (
    <SPThemeProvider value={theme}>
      <div className={cx('sp-journey', 'sp-shell', dark && 'sp-theme-dark', railCollapsed && 'is-rail-collapsed')}>
        {/* Rail / drawer */}
        <AnimatePresence>
          {navOpen && (
            <motion.div
              className="sp-shell__scrim"
              variants={fade}
              initial="hidden"
              animate="show"
              exit="exit"
              onClick={() => setNavOpen(false)}
            />
          )}
        </AnimatePresence>

        <aside className={cx('sp-shell__rail', navOpen && 'is-open')}>
          <div className="sp-shell__brand">
            <img
              src={Images.tpaLogo}
              alt="The Purpose Academy"
              className={dark ? '' : 'invert brightness-90 hue-rotate-[220deg]'}
            />
          </div>

          <div className="sp-shell__rail-scroll sp-scroll">
            <JourneyNav items={items} onNavigate={() => setNavOpen(false)} isSkillProfileLocked={isSkillProfileLocked} />
          </div>

          <div className="sp-shell__rail-foot">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 6px 10px', minWidth: 0 }}>
              <div
                style={{
                  width: 32, height: 32, borderRadius: '9999px', flexShrink: 0,
                  background: 'var(--sp-primary-soft)', color: 'var(--sp-primary)',
                  display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 13,
                }}
              >
                {user?.name?.[0]?.toUpperCase() || 'S'}
              </div>
              <div style={{ minWidth: 0 }}>
                <div className="sp-body-sm" style={{ color: 'var(--sp-text)', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user?.name}
                </div>
                <div className="sp-caption" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {dashboard?.cohort?.title || t('shell.fellowship', 'Fellowship')}
                </div>
              </div>
            </div>
            <button type="button" className="sp-btn sp-btn--ghost sp-btn--sm sp-btn--block" onClick={handleLogout}>
              <LogOut size={14} /> {t('shell.logout', 'Log out')}
            </button>
          </div>
        </aside>

        {/* Main */}
        <div className="sp-shell__main">
          <JourneyTopBar onToggleNav={handleToggleNav} onLogout={handleLogout} />
          <main className="sp-shell__content">
            <Suspense fallback={<div style={{ display: 'grid', placeItems: 'center', minHeight: 300, opacity: 0.6 }}><span className="sp-caption">{t('shell.loading', 'Loading…')}</span></div>}>
              {/* These paths render CLASSIC pages by route config; swap in the
                  Journey view here so the CLASSIC route stays untouched. */}
              {location.pathname === '/student/skill-profile' ? (
                <JourneySkillProfile />
              ) : location.pathname === '/student/profile' ? (
                <JourneyProfile />
              ) : (
                <Outlet />
              )}
            </Suspense>
          </main>
        </div>

        <JourneyBottomNav items={items} pathname={location.pathname} isSkillProfileLocked={isSkillProfileLocked} />
      </div>
    </SPThemeProvider>
  );
};
